<template>
  <CustomModal
    v-model:visible="visible"
    title="Manage credit limit"
    width="32vw"
  >
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-2">
        <span class="user_label">User</span>
        <div class="flex items-center gap-3">
          <ProfileIcon v-if="row" :data="row" :size="40" :font="14" />
          <span class="text-base text_color">{{ displayName }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <a-checkbox v-model:checked="noCreditLimit">No credit limit</a-checkbox>

        <div v-if="!noCreditLimit" class="flex flex-col gap-1">
          <span class="field_label pt-2">
            Credit limit (max usage for 30 days)
          </span>
          <a-input-number
            v-model:value="creditLimit"
            :min="1"
            :step="100"
            :controls="false"
            placeholder="Enter credit limit"
            class="w-full"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <a-button class="secondary_medium" @click="handleCancel">
        Cancel
      </a-button>
      <a-button class="primary_medium" :loading="saving" @click="handleSave">
        Save changes
      </a-button>
    </template>
  </CustomModal>
</template>

<style scoped>
:deep(.modal_title) {
  line-height: 19px;
}

:deep(.modal_footer) {
  display: flex !important;
  padding: 16px !important;
  justify-content: space-between !important;
  align-items: flex-start !important;
  align-self: stretch !important;
  border-top: 1px solid var(--white-opacity-8) !important;
}

.user_label {
  color: var(--secondary-color);
  font-family: "Proxima Nova", sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 15px;
}

.field_label {
  color: var(--secondary-color);
  font-size: 14px;
}
</style>

<script setup>
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import CustomModal from "@/src/components/CustomModal.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import BillingServiceV4 from "@/src/api/BillingServiceV4.js";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  row: { type: Object, default: null },
});

const emit = defineEmits(["update:modelValue", "updated"]);

const visible = ref(props.modelValue);
const noCreditLimit = ref(true);
const creditLimit = ref(null);

const displayName = computed(() => {
  const row = props.row;
  if (!row) return "";
  const full = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  return row.name || full || row.email || "";
});

watch(
  () => props.modelValue,
  (v) => {
    visible.value = v;
    if (v) {
      if (props.row?.credit_limit != null) {
        noCreditLimit.value = false;
        creditLimit.value = props.row.credit_limit;
      } else {
        noCreditLimit.value = true;
        creditLimit.value = null;
      }
    }
  }
);

watch(visible, (v) => emit("update:modelValue", v));

const handleCancel = () => {
  visible.value = false;
};

const saving = ref(false);

const handleSave = async () => {
  if (
    !noCreditLimit.value &&
    (creditLimit.value == null || creditLimit.value <= 0)
  ) {
    message.error("Please enter a valid credit limit");
    return;
  }
  if (!props.row?.id) {
    message.error("Missing user context");
    return;
  }
  const isEnabled = !noCreditLimit.value;
  const payload = {
    monthly_limit_credits: isEnabled ? Number(creditLimit.value) : 0,
    is_enabled: isEnabled,
  };
  saving.value = true;
  try {
    await BillingServiceV4.upsertUserBudget(props.row.id, payload);
    const newLimit = isEnabled ? Number(creditLimit.value) : null;
    message.success(
      newLimit == null
        ? "Credit limit removed"
        : `Credit limit set to ${newLimit.toLocaleString()} credits`
    );
    emit("updated", { userId: props.row.id, credit_limit: newLimit });
    visible.value = false;
  } catch (err) {
    console.error("Failed to save credit limit:", err);
    message.error(
      err?.response?.data?.error || "Failed to save credit limit"
    );
  } finally {
    saving.value = false;
  }
};
</script>
